import { FC, useCallback, useEffect, useMemo } from 'react';
import { Transition } from '@headlessui/react';
import { MailIcon } from '@heroicons/react/outline';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import {
  Feature,
  MailSequenceEnrollmentStatus,
  PatientNameDataFragment,
  useMailSequenceEnrollmentsForProgramQuery,
} from '../../../../generated/graphql';

import { useFeatureFlags } from '../../../../contexts/FeatureFlagContext';
import { defaultTransitionProps } from '../../../lib/animation';
import { customToast } from '../../../components/ToastAlert/customToast';
import Button from '../../../components/Button';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import IllustrationMail from '../../../svgs/IllustrationMail';
import Spinner from '../../../svgs/Spinner';
import { sequenceDisplayTitle } from '../../../lib/mailSequence';
import {
  SCHEDULE_MAIL_PATH,
  ScheduleMailLocationState,
} from '../../ScheduleMail/types';

import { usePatientProfileContext } from '..';

import SequenceListItem from './SequenceListItem';

const STATUS_ORDER: Record<MailSequenceEnrollmentStatus, number> = {
  [MailSequenceEnrollmentStatus.Active]: 0,
  [MailSequenceEnrollmentStatus.Completed]: 1,
  [MailSequenceEnrollmentStatus.Cancelled]: 2,
};

const sortEnrollments = <
  T extends {
    status: MailSequenceEnrollmentStatus;
    referenceDate: string;
  },
>(
  enrollments: T[],
): T[] =>
  [...enrollments].sort((a, b) => {
    const s = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (s !== 0) return s;
    return a.referenceDate < b.referenceDate ? 1 : -1;
  });

const Sequences: FC = () => {
  const { patient } = usePatientProfileContext();
  const params = useParams();
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const isMailSequencesEnabled = isFeatureEnabled(Feature.MailSequences);
  const programId = params.programId!;
  const enrollmentId = params.enrollmentId;

  const openSequenceBuilder = useCallback(() => {
    if (!patient?.programInstanceId) {
      navigate('/clients/active');
      return;
    }

    const recipientPatient: PatientNameDataFragment = {
      __typename: 'PatientName',
      id: patient.id,
      firstName: patient.firstName,
      lastName: patient.lastName,
      name: patient.name,
      email: patient.email,
      programInstanceId: patient.programInstanceId,
      createdAt: patient.createdAt,
      isTestProgram: patient.isTestProgram,
      programStartDate: patient.programStartDate,
      programEndDate: patient.programEndDate,
      profileImageMedia: patient.profileImageMedia,
    };

    const state: ScheduleMailLocationState = {
      programIds: [patient.programInstanceId],
      recipientPatients: [recipientPatient],
      returnTo: `/clients/${patient.programInstanceId}/sequences`,
    };
    navigate(SCHEDULE_MAIL_PATH, { state });
  }, [navigate, patient]);

  const { data, loading, error, refetch } =
    useMailSequenceEnrollmentsForProgramQuery({
      variables: { programId },
      fetchPolicy: 'cache-and-network',
      onCompleted: (result) => {
        const all = result.mailSequenceEnrollmentsForProgram ?? [];
        const isRootFetch = !enrollmentId;
        if (isRootFetch && all.length > 0) {
          const sorted = sortEnrollments(all);
          navigate(sorted[0].id, { replace: true });
        }
      },
      onError: (err) => {
        customToast.error(err.message || 'Could not load mail sequences.');
      },
    });

  const enrollments = useMemo(
    () => sortEnrollments(data?.mailSequenceEnrollmentsForProgram ?? []),
    [data?.mailSequenceEnrollmentsForProgram],
  );

  useEffect(() => {
    if (
      enrollmentId &&
      data &&
      !loading &&
      enrollments.length > 0 &&
      !enrollments.some((e) => e.id === enrollmentId)
    ) {
      navigate(enrollments[0].id, { replace: true });
    }
  }, [enrollmentId, data, loading, enrollments, navigate]);

  const hasEnrollments = enrollments.length > 0;

  return (
    <>
      <Transition
        show={Boolean(data && !error)}
        {...defaultTransitionProps}
        className="flex h-full w-full flex-grow flex-row items-start border-neutral-75"
      >
        {!hasEnrollments ? (
          <EmptyProfileTab
            IconComponent={IllustrationMail}
            iconClassName="text-secondary-100"
            titleText={`${
              patient?.firstName ?? 'This client'
            } isn't enrolled in any sequences yet`}
            subTitleText={
              isMailSequencesEnabled
                ? 'Create a Mail sequence to start enrolling clients to receive scheduled Mail.'
                : 'Mail sequences are not enabled for this practice.'
            }
            onClickButton={
              isMailSequencesEnabled ? openSequenceBuilder : undefined
            }
            buttonTitle="New Mail sequence"
            ButtonIconComponent={MailIcon}
          />
        ) : (
          <>
            <div className="flex h-full w-1/6 min-w-[188px] flex-col items-center justify-start border-r border-neutral-75">
              {isMailSequencesEnabled && (
                <div className="flex w-full flex-row items-center justify-center py-4 pr-5">
                  <Button
                    IconComponent={MailIcon}
                    noFocus
                    title="New Mail sequence"
                    iconClassName="h-[18px] w-[18px]"
                    size="small"
                    className="mt-1 ml-1 w-[194px] justify-between rounded-md pl-5 text-left"
                    onClick={openSequenceBuilder}
                  />
                </div>
              )}

              <div
                className={`flex h-full w-full flex-col items-center justify-start gap-y-2 overflow-y-auto overscroll-y-contain pb-4 pr-4 ${
                  isMailSequencesEnabled ? '' : 'pt-4'
                }`}
              >
                {enrollments.map((enrollment) => (
                  <SequenceListItem
                    key={enrollment.id}
                    label={sequenceDisplayTitle(enrollment)}
                    referenceDate={enrollment.referenceDate}
                    scheduledDates={enrollment.scheduledSends.map(
                      (send) => send.scheduledDate,
                    )}
                    mailCount={enrollment.scheduledSends.length}
                    status={enrollment.status}
                    isSelected={enrollmentId === enrollment.id}
                    onClick={() => navigate(enrollment.id)}
                  />
                ))}
              </div>
            </div>

            <div className="h-full w-full overflow-hidden">
              {enrollmentId ? (
                <Outlet context={{ refetchEnrollments: refetch }} />
              ) : null}
            </div>
          </>
        )}
      </Transition>

      {loading && !data && <Spinner className="mx-auto mt-8" />}
    </>
  );
};

export default Sequences;
